
import React from "react";
import { Theme } from "@/hooks/use-theme";
import { cn } from "@/lib/utils";
import { CheckCircle } from "lucide-react";

interface ThemePreviewProps {
  theme: Theme;
  currentTheme: Theme;
  onClick: (theme: Theme) => void;
  name: string;
}

export const ThemePreview: React.FC<ThemePreviewProps> = ({
  theme,
  currentTheme,
  onClick,
  name
}) => {
  const isSystemTheme = theme === "system";
  const isSelected = currentTheme === theme;
  
  let previewClassName = "";
  
  // Apply specific theme classes to the preview
  switch (theme) {
    case "light":
      previewClassName = "bg-white border border-gray-200";
      break;
    case "dark":
      previewClassName = "bg-black border border-clubbers-blue";
      break;
    case "ocean":
      previewClassName = "bg-[hsl(200,70%,15%)] border border-[hsl(196,100%,42%)]";
      break;
    case "sunset":
      previewClassName = "bg-[hsl(20,60%,15%)] border border-[hsl(20,100%,50%)]";
      break;
    case "forest":
      previewClassName = "bg-[hsl(120,30%,15%)] border border-[hsl(140,70%,40%)]";
      break;
    case "system":
      previewClassName = "bg-gradient-to-r from-white to-clubbers-dark border border-gray-200";
      break;
  }
  
  return (
    <div 
      className={cn(
        "relative flex flex-col items-center justify-between rounded-xl p-2",
        "cursor-pointer hover:scale-105 transition-all duration-300",
        isSelected ? "ring-2 ring-primary " : "",
        previewClassName
      )}
      onClick={() => onClick(theme)}
    >
      {/* Theme visual preview */}
      <div className="w-full h-16 rounded-lg flex flex-col justify-center items-center">
        {/* Header preview */}
        <div className={`w-8 h-2 mb-1 rounded ${theme === "light" ? "bg-black/10" : "bg-white/20"}`}></div>
        
        {/* Content preview */}
        <div className={`w-12 h-3 rounded ${theme === "light" ? "bg-black/10" : "bg-white/20"}`}></div>
      </div>
      
      {/* Theme name */}
      <span className={`text-xs font-medium mt-2 ${theme === "light" ? "text-black" : "text-white"}`}>
        {name}
      </span>
      
      {/* Selected indicator */}
      {isSelected && (
        <div className="absolute top-1 right-1 text-primary">
          <CheckCircle className="w-4 h-4" />
        </div>
      )}
    </div>
  );
};
